import { useTranslation, untranslated } from "@i18n";
import { useState, useRef, useCallback, type MouseEvent } from "react";
import { useDispatch } from "react-redux";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UsersIcon from "@Assets/icons/svg/usersIcon.svg?react";
import AddIcon from "@Assets/icons/Create.svg?react";
import { FileManagerAsPopup } from "@Features/files";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SpaceUserSelector from "./SpaceUserSelector";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import NoDataSvg from "@CubeComponents/html/NoData";
import { spacesDataUrl } from "@Services/dataManager";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { showToastNotification } from "@Reducers/toastSlice";
import { formatBytes, SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { convertDate } from "@Services/luxonHelper";
import { getFormTemplate } from "@Actions/fileUpload";
import { saveTransmitalsData, setTransmittalDataForReviews } from "@Features/transmittals";
import { useFormValidator } from "@Features/forms";
import { getFileImage } from "@Services/utils/iconMap";
import IconComponent from "@CubeComponents/icons";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Text from "@CubeComponents/html/Text/Text";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import Box from "@mui/material/Box";
import useCreateTransmittalForm from "../../../hooks/useCreateTransmittalForm";
import { defaultModulesMapper } from "@Helpers/constants";
import type { SaveTransmittalsRequest } from "@ServerTypes/transmittals/transmittals.schema";

interface Space {
  _id: string;
  description: string;
  spaceName: string;
  createdDate: number;
  status: string;
  id: string;
  createdBy: string;
  spaceId: string;
  projectId: string;
  createdByName: string;
}

type _OriginSpace = Pick<Space, "_id" | "spaceName">;

interface CreateTransmittalProps {
  selectedSpaceId?: string | null;
  selectedReferences?: any | null;
  /** Forwarded to useCreateTransmittalForm, which sends it as `reviewData.reviewId`. */
  reviewId?: NonNullable<SaveTransmittalsRequest["reviewData"]>["reviewId"] | null;
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface SelectedUsers {
  id: string;
  spaceId: string;
  spaceName: string;
}

// Web API adapters for useCreateTransmittalForm
const createWebApi = (projectId: string) => ({
  fetchSpaces: async () => {
    const query = new QueryBuilder().addParams("projectId", projectId).addParams("allProjectSpaces", true);
    const result = await executeQuery<Space>(spacesDataUrl, query);
    return result.result || [];
  },
  fetchFormTemplate: async (projId: string, resourceType: string) => {
    const response = await getFormTemplate({ projectId: projId, resourceType });
    if (response.data.status && response.data.data) {
      return response.data.data as any;
    }
    return null;
  },
  saveTransmittal: async (data: any) => {
    return saveTransmitalsData(data);
  },
});

function CreateTransmittal({
  selectedSpaceId = null,
  selectedReferences = null,
  reviewId = null,
  projectId,
  isOpen,
  onClose,
}: CreateTransmittalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formValidator = useFormValidator({});
  const referencesRef = useRef<any>(null);

  // Web API adapters
  const webApi = useRef(createWebApi(projectId)).current;

  // Validate custom fields callback for the hook
  const validateCustomFields = useCallback(() => {
    return formValidator.validateAll();
  }, [formValidator]);

  // Use platform-agnostic form hook
  const {
    // Loading states
    isLoading,
    isSubmitting,
    // Space state
    originSpace,
    filteredSpaceList,
    // Form fields
    transmittalSubject,
    transmittalRemarks,
    formTemplate,
    // User selection
    selectedToUsers,
    selectedNotifyUsers,
    toUsersBySpace,
    notifyUsersBySpace,
    hasToUsers,
    hasNotifyUsers,
    // Enable states
    isToSpaceEnabled,
    isNotifySpaceEnabled,
    // References (from hook)
    references: transmittalReferences,
    referencesDataset,
    alreadyAttachedIds: alreadyAttachedResources,
    addReferences,
    removeReferences,
    updateReferenceRemark: updateRemark,
    // Validation
    canSubmit: enableSendTransmittal,
    hasMinimumSpaces,
    // Actions
    setSubject,
    setRemarks,
    handleFormData,
    setToUsers,
    setNotifyUsers,
    removeToUser,
    removeNotifyUser,
    changeOriginSpace,
    submit,
    reset,
  } = useCreateTransmittalForm({
    projectId,
    selectedSpaceId,
    selectedReferences,
    reviewId,
    isOpen,
    api: webApi,
    validateCustomFields,
    callbacks: {
      onSuccess: (response: any) => {
        dispatch(
          showToastNotification({
            content: t("TS-{{counter}} has been sent successfully!", { counter: response?.counter }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        if (reviewId) {
          dispatch(setTransmittalDataForReviews(true));
        }
        clearFormData();
        onClose();
      },
      onError: (error: string) => {
        dispatch(
          showToastNotification({
            content: untranslated(error || SOMETHING_WENT_WRONG),
            type: TOAST_TYPE_ERROR,
          }),
        );
      },
    },
  });

  // Component States (UI-specific only)
  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const enableOriginSpace = !selectedSpaceId;
  const [isToSelectorOpen, setIsToSelectorOpen] = useState<boolean>(false);
  const [isNotifySelectorOpen, setIsNotifySelectorOpen] = useState<boolean>(false);
  const [isReferencesManagerOpen, setIsReferencesManagerOpen] = useState<boolean>(false);
  const [selectedRowCount, setSelectedRowCount] = useState<number>(0);
  const [userHasNoSpaces, setUserHasNoSpaces] = useState<boolean>(false);

  // Origin Space Dropdown component

  const OriginSpaceDropDownList = () => (
    <SelectSpaces
      moduleName={defaultModulesMapper.TRANSMITTALS}
      workspaceId={selectedSpaceId}
      disablePermissionCheck={true}
      isDisabled={!enableOriginSpace || userHasNoSpaces}
      data-testid="spaceDropdownField"
      projectId={projectId}
      selectedSpaceCallback={(params: any, spaceData) => {
        changeOriginSpace(params._id, spaceData);
      }}
      fetchSpaceListCallback={(response: any) => {
        const result = response.data?.result;
        setUserHasNoSpaces(!result || result.length === 0);
      }}
    />
  );

  // Handler functions

  const handleToClick = () => {
    setIsToSelectorOpen(true);
  };

  const handleNotifyClick = () => {
    setIsNotifySelectorOpen(true);
  };

  const handleReferencesPopup = () => {
    setIsReferencesManagerOpen(!isReferencesManagerOpen);
  };

  const handleReferencesDelete = () => {
    if (referencesRef.current && referencesRef.current.slickGrid) {
      const selectedRows = referencesRef.current.slickGrid.getSelectedRows();
      if (selectedRows.length === 0) {
        dispatch(
          showToastNotification({
            content: t("Please select at least one reference to delete"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }

      const selectedItems = selectedRows.map((rowIdx: any) => referencesRef.current.dataView.getItem(rowIdx));
      const idsToRemove = selectedItems.map((item: any) => item.id);

      // Use hook's removeReferences method
      removeReferences(idsToRemove);

      referencesRef.current.slickGrid.setSelectedRows([]);

      dispatch(
        showToastNotification({
          content: t("{{count}} reference(s) removed successfully", { count: selectedRows.length }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
  };

  const handleReferencesPopupCancelClick = () => {
    setIsReferencesManagerOpen(false);
  };

  const handleReferencesSelectedButtonClick = async (data: any) => {
    // Convert data to array format for hook's addReferences
    const newReferences = Object.keys(data).map((key) => ({
      fileVersion: data[key].fileVersion,
      name: data[key].name,
      versionNumber: data[key].versionNumber,
      size: data[key].size,
      dateCreated: data[key].dateCreated,
      resourceType: data[key].resourceType,
      fileType: data[key].fileType,
      remarks: data[key].remarks || "",
      ...(data[key].transmittalVersionId && {
        transmittalVersionId: data[key].transmittalVersionId,
      }),
    }));

    // Use hook's addReferences method (preserves existing remarks)
    addReferences(newReferences);
    setIsReferencesManagerOpen(false);
  };

  const handleReferencesGridClick = () => {};

  const handleSelectionChanged = () => {
    if (referencesRef.current && referencesRef.current.slickGrid) {
      const selectedRows = referencesRef.current.slickGrid.getSelectedRows();
      setSelectedRowCount(selectedRows.length);
    }
  };

  const handleBackdropClick = (e: MouseEvent<HTMLElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleMaximize = () => {
    setIsMaximized((prev) => !prev);
  };

  const handleTransmittalRemarksChange = (value: any) => {
    setRemarks(value);
  };

  const handleSpaceUserSelection = (users: SelectedUsers, type: string) => {
    if (type === "To") {
      setToUsers(users as any);
    } else if (type === "Notify") {
      setNotifyUsers(users as any);
    }
  };

  const handleRemoveUser = (id: string, type: string) => {
    if (type === "To") {
      removeToUser(id);
    } else if (type === "Notify") {
      removeNotifyUser(id);
    }
  };

  const clearFormData = () => {
    // reset() clears all form state including references
    reset();
  };

  // Grid Templates, Column Definition and Tool Bar

  const addReferenceButton = () => (
    <Box className="ms-4">
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={() => handleReferencesPopup()}>
        <span className="flex items-center justify-start">
          <IconComponent name="add" width="1rem" height="1rem" />
          <Text>{t("Add")}</Text>
        </span>
      </ButtonComponent>
    </Box>
  );

  const deleteReferenceButton = () => (
    <Box className="ms-4">
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={() => handleReferencesDelete()}>
        <span className="flex items-center justify-start">
          <IconComponent name="delete" width="1.25rem" height="1.25rem" />
          <Text>{t("Delete")}</Text>
        </span>
      </ButtonComponent>
    </Box>
  );

  const referencesGridToolbar = [
    {
      id: "add",
      template: addReferenceButton,
    },
    {
      id: "delete",
      template: deleteReferenceButton,
      disabled: selectedRowCount === 0,
    },
  ];

  const nameTemplate = (_row: any, _cell: any, _value: any, _col: any, args: any) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const root = createRoot(container);
    const FileIcon = getFileImage(args);
    const component = args.id ? (
      <>
        {args.name ? (
          <Box className="flex items-center text-ellipsis overflow-hidden whitespace-nowrap px-2 py-4 gap-2">
            {typeof FileIcon === "string" ? (
              <img src={FileIcon} className="w-5 h-5" alt="" />
            ) : (
              <FileIcon className="w-5 h-5" />
            )}
            <span className="text-sm font-normal text-primary_text">{args.name}</span>
          </Box>
        ) : null}
      </>
    ) : null;
    root.render(component);
    return container;
  };

  const versionTemplate = (_row: any, _cell: any, _value: any, _col: any, args: any) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const root = createRoot(container);
    const component = args.id ? (
      <>
        {args.versionNumber ? (
          <Box className="flex items-center text-ellipsis overflow-hidden whitespace-nowrap px-2 py-4 gap-2">
            <span className="text-sm font-normal text-primary_text">V{args.versionNumber}</span>
          </Box>
        ) : null}
      </>
    ) : null;
    root.render(component);
    return container;
  };

  const sizeTemplate = (_row: any, _cell: any, _value: any, _col: any, args: any) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const root = createRoot(container);
    const component = args.id ? (
      <>
        {args.size ? (
          <Box className="flex items-center text-ellipsis overflow-hidden whitespace-nowrap px-2 py-4 gap-2">
            <span className="text-sm font-normal text-primary_text">{formatBytes(args.size)}</span>
          </Box>
        ) : null}
      </>
    ) : null;
    root.render(component);
    return container;
  };

  const dateTemplate = (_row: any, _cell: any, _value: any, _col: any, args: any) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const root = createRoot(container);
    const component = args.id ? (
      <>
        {args.dateCreated ? (
          <Box className="flex items-center text-ellipsis overflow-hidden whitespace-nowrap px-2 py-4 gap-2">
            <span className="text-sm font-normal text-primary_text">
              {convertDate(args.dateCreated, "dateForText")}
            </span>
          </Box>
        ) : null}
      </>
    ) : null;
    root.render(component);
    return container;
  };

  const activeCellRef = useRef<any>({ rowId: null, field: null });

  const remarksTemplate = (_row: any, _cell: any, _value: any, _col: any, args: any) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    container.addEventListener("click", (e) => e.stopPropagation());
    container.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === " " || e.code === "Space") {
        e.stopPropagation();
      }
    });
    const autoFocus = activeCellRef.current.field === "remarks" && activeCellRef.current.rowId === args.id;

    root.render(
      <TextFieldComponent
        defaultValue={args.remarks || ""}
        value={args.remarks}
        name={"remarks"}
        autoFocus={autoFocus}
        onFocus={() => {
          activeCellRef.current = { rowId: args.id, field: "remarks" };
        }}
        onChange={(e) => {
          updateRemark(args.id, e.target.value);
          const currentRowData = referencesRef.current.dataView.getItemById(args.id);
          referencesRef.current.dataView.updateItem(args.id, {
            ...currentRowData,
            remarks: e.target.value,
          });
          const rowIndex = referencesRef.current.dataView.getRowById(args.id);
          const columnIndex = referencesRef.current.slickGrid.getColumnIndex("remarks");
          referencesRef.current.slickGrid.updateCell(rowIndex, columnIndex);
        }}
        placeholder={t("Enter Remarks")}
      />,
    );
    return container;
  };

  const referencesGridColumnDefinitions = useRef([
    {
      id: "name",
      field: "name",
      name: t("Name"),
      formatter: nameTemplate,
    },
    {
      id: "fileType",
      field: "fileType",
      name: t("File Type"),
    },
    {
      id: "versionNumber",
      field: "versionNumber",
      name: t("Version"),
      formatter: versionTemplate,
      _excelValueGetter: (row: any) => (row.versionNumber ? `V${row.versionNumber}` : ""),
    },
    {
      id: "size",
      field: "size",
      name: t("Size"),
      formatter: sizeTemplate,
      _excelValueGetter: (row: any) => (row.size ? formatBytes(row.size) : ""),
    },
    {
      id: "dateCreated",
      field: "dateCreated",
      name: t("Added On"),
      formatter: dateTemplate,
      _excelValueGetter: (row: any) => (row.dateCreated ? convertDate(row.dateCreated, "dateForText") : ""),
    },
    {
      id: "remarks",
      field: "remarks",
      name: t("Remarks"),
      formatter: remarksTemplate,
    },
  ]);

  // JSX Sections

  const modalHeader = () => (
    <Box className="flex items-center justify-between w-full py-2 px-6 bg-surface_stroke_gradient_color1 rounded-t-lg">
      <Text className="text-base font-bold">{t("Create Transmittal")}</Text>
      <Box className="flex items-center gap-4 ms-auto">
        {OriginSpaceDropDownList()}
        <IconComponent name="maximizeIcon" width="1.5rem" height="1.5rem" onClick={handleMaximize} />
        <IconComponent name="close" width="0.9375rem" height="0.9375rem" onClick={onClose} className="cursor-pointer" />
      </Box>
    </Box>
  );
  const modalContent = () => {
    if (isLoading) {
      return (
        <Box className="flex justify-center items-center h-full w-full p-6">
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (userHasNoSpaces) {
      return (
        <Box className="flex justify-center items-center">
          <NoDataSvg
            text1={t("You cannot create a transmittal as you are not a part of any space.")}
            height={300}
            width={300}
          />
        </Box>
      );
    }

    if (!hasMinimumSpaces) {
      return (
        <Box className="flex justify-center items-center">
          <NoDataSvg
            text1={t(
              "A transmittal cannot be created in this project since there is only one space. A minimum of two spaces are required.",
            )}
            height={300}
            width={300}
          />
        </Box>
      );
    }

    return (
      <Box className="flex bg-surface_white p-6 flex-col items-start gap-6 self-stretch">
        {/* To Users Section */}
        <Box className="flex flex-col items-start p-4 gap-4 rounded-lg border border-surface_stroke_gradient_color1 w-full">
          <Box className="flex gap-4 items-center">
            <Text className="font-semibold text-base">
              {t("To")}
              <span className="text-red-500"> *</span>
            </Text>
            <ButtonComponent
              buttonType={BUTTON_TYPES.INFO_BACKGROUND_BUTTON}
              onClick={handleToClick}
              disabled={!isToSpaceEnabled}
            >
              <span className="flex flex-wrap items-center justify-start gap-2">
                <UsersIcon className="my-1.5 mx-0 rounded-2xl h-4 w-4" />
              </span>
            </ButtonComponent>
          </Box>
          {hasToUsers &&
            Object.entries(toUsersBySpace).map(([spaceId, users]) => (
              <Box className="flex gap-4 items-center" key={spaceId}>
                <Box className="text-primary_text font-semibold text-sm">{users[0]?.spaceName}</Box>
                <Box className="flex flex-wrap gap-4">
                  {users.map((user: any) => (
                    <UserNameTagComponent
                      key={user.id}
                      id={user.id.split("_")[0]}
                      handleDeleteIconClicked={() => handleRemoveUser(user.id, "To")}
                    />
                  ))}
                </Box>
              </Box>
            ))}
        </Box>

        {/* Notify Users Section */}
        <Box className="flex flex-col items-start p-4 gap-4 rounded-lg border border-surface_stroke_gradient_color1 w-full">
          <Box className="flex gap-4 items-center">
            <Text className="font-semibold text-base">{t("Notify")}</Text>
            <ButtonComponent
              buttonType={BUTTON_TYPES.INFO_BACKGROUND_BUTTON}
              onClick={handleNotifyClick}
              disabled={!isNotifySpaceEnabled}
            >
              <span className="flex flex-wrap items-center justify-start gap-2">
                <UsersIcon className="my-1.5 mx-0 rounded-2xl h-4 w-4" />
              </span>
            </ButtonComponent>
          </Box>
          {hasNotifyUsers &&
            Object.entries(notifyUsersBySpace).map(([spaceId, users]) => (
              <Box className="flex gap-4 items-center" key={spaceId}>
                <Box className="text-primary_text font-semibold text-sm">{users[0]?.spaceName}</Box>
                <Box className="flex flex-wrap gap-4">
                  {users.map((user: any) => (
                    <UserNameTagComponent
                      key={user.id}
                      id={user.id.split("_")[0]}
                      handleDeleteIconClicked={() => handleRemoveUser(user.id, "Notify")}
                    />
                  ))}
                </Box>
              </Box>
            ))}
        </Box>

        {/* To Selector Component */}
        {isToSelectorOpen && (
          <SpaceUserSelector
            spaceList={filteredSpaceList as any}
            onSpaceSelectorModalClose={() => setIsToSelectorOpen(false)}
            onConfirm={(users: SelectedUsers[]) => handleSpaceUserSelection(users as any, "To")}
            selectedUsers={selectedToUsers as any}
          />
        )}

        {/* Notify Selector Component */}
        {isNotifySelectorOpen && (
          <SpaceUserSelector
            spaceList={filteredSpaceList as any}
            onSpaceSelectorModalClose={() => setIsNotifySelectorOpen(false)}
            onConfirm={(users: SelectedUsers[]) => handleSpaceUserSelection(users as any, "Notify")}
            selectedUsers={selectedNotifyUsers as any}
          />
        )}

        {/* Subject and Body Text Section */}
        <Box className="flex flex-col items-start p-4 gap-4 rounded-lg border border-surface_stroke_gradient_color1 w-full overflow-x-hidden">
          <Box className="flex items-center w-full">
            <Text className="font-semibold text-base w-[6.25rem] flex-shrink-0">
              {t("Subject")}
              <span className="text-red-500"> *</span>
            </Text>
            <TextFieldComponent
              value={transmittalSubject}
              onChange={(e) => setSubject(e.target.value)}
              name="subject"
              placeholder={t("Enter Subject")}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                },
              }}
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
          <Box className="flex w-full">
            <Text className="font-semibold text-base w-[6.25rem] flex-shrink-0">{t("Body Text")}</Text>
            <Box className="w-full overflow-hidden">
              {isOpen && (
                <SimpleEditor
                  key={`editor-${isOpen}`}
                  showCharCount={true}
                  maxLength={255}
                  showToolbar={true}
                  height="150"
                  value={transmittalRemarks}
                  onChange={handleTransmittalRemarksChange}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* References Section */}
        {referencesModal()}

        {/* Custom Fields Section */}
        <Box className="w-full">
          {formTemplate && (
            <FormPreview
              formValidator={formValidator}
              formData={formTemplate}
              disabled={false}
              fullWidthInput
              handleData={handleFormData}
            />
          )}
        </Box>
      </Box>
    );
  };

  const referencesModal = () => (
    <Accordion
      defaultExpanded
      disableGutters
      sx={{
        width: "100%",
        boxShadow: "none",
        "&:before": { display: "none" },
        border: "1px solid #ECF3FE",
        borderRadius: "0.5rem",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          padding: "1rem",
          "& .MuiAccordionSummary-content": {
            margin: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          },
        }}
      >
        <Box className="flex items-center gap-4">
          <Text className="font-semibold text-base">
            {t("References")}
            <span className="text-red-500"> *</span>
          </Text>
          {Object.keys(transmittalReferences).length === 0 ? (
            <Box>
              <ButtonComponent
                buttonType={BUTTON_TYPES.INFO_BACKGROUND_BUTTON}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReferencesPopup();
                }}
              >
                <span className="flex flex-wrap items-center justify-start gap-2">
                  <AddIcon className="my-1.5 mx-0 rounded-2xl h-4 w-4" />
                </span>
              </ButtonComponent>
            </Box>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "1rem" }}>{getReferencesGrid()}</AccordionDetails>
    </Accordion>
  );

  const getReferencesGrid = () => {
    if (isLoading) {
      return (
        <Box className="flex justify-center items-center h-full w-full p-6">
          <CircularProgress size={40} />
        </Box>
      );
    }

    return (
      <Box className="overflow-auto h-full w-full">
        <Box
          style={{
            opacity: referencesDataset.length > 0 ? 1 : 0,
            height: referencesDataset.length > 0 ? "30vh" : 0,
          }}
        >
          <SlickGrid1
            ref={referencesRef}
            columnDefinitions={referencesGridColumnDefinitions.current as any}
            dataset={referencesDataset}
            onClick={handleReferencesGridClick}
            toolbar={referencesGridToolbar}
            onSelectedRowIdsChanged={handleSelectionChanged}
            selectCheckBoxOnRowClick
            enableFilteringOption
            enableSortingOption
            enableGroupingOption
            enableColumnChooser
            excelExport
            hideNoDataIllustration
          />
        </Box>
        {referencesDataset.length === 0 && <NoDataSvg text1={t("No references attached")} height={200} width={200} />}
      </Box>
    );
  };

  const modalFooter = () => (
    <Box className="flex w-full py-4 px-6 bg-surface_white border-t border-surface_stroke_gradient_color1 gap-4 rounded-b-lg">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={submit}
        disabled={!enableSendTransmittal || !hasMinimumSpaces || userHasNoSpaces || isLoading}
        loading={isSubmitting}
      >
        <Text>{t("Send")}</Text>
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={onClose}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </Box>
  );

  if (!isOpen) return null;

  return (
    <Box
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {isReferencesManagerOpen && (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={handleReferencesSelectedButtonClick}
          handleFileSelectedCancelClick={handleReferencesPopupCancelClick}
          workspaceId={originSpace?._id}
          minimumFiles={1}
          hideExternalSpace
          isTransmittal
          hideIssues
          hideTasks
          hideReviews
          hideEstimates
          hideClashTests
          hideSchedules
          hideContracts
          enableOnlyShareableResources={true}
          disableFiles={alreadyAttachedResources}
          enableFilesTypes={["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FOLDER"]}
          heading={`Attach References - ${originSpace?.spaceName}`}
          id="fileManagerAsPopup"
        />
      )}
      <Box
        className={`bg-white rounded-lg shadow-lg flex flex-col transition-all duration-300 ease-in-out ${
          isMaximized ? "w-full h-full max-w-none" : "w-3/5 max-h-[90vh]"
        }`}
      >
        {modalHeader()}
        <Box className="flex-1 overflow-y-auto">{modalContent()}</Box>
        {modalFooter()}
      </Box>
    </Box>
  );
}

export default CreateTransmittal;
